import React, { useState, useEffect } from 'react'
import { parseDocument } from 'htmlparser2'
import Header from './Header'
import FooterDynamic from './FooterDynamic'
import { Page, Text, View, StyleSheet, Font, Link } from '@react-pdf/renderer'

Font.registerHyphenationCallback(word => [word]);

const styles = StyleSheet.create({
    categoryText: {
        paddingLeft: '0%',
        paddingRight: '5%',
        marginTop: 45,
    },
    tableHeader: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        paddingLeft: '5%',
        paddingRight: '5%',
        backgroundColor: 'rgb(226 232 240)',
    },
    tableHeaderTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        paddingTop: 5,
        paddingBottom: 5,
    },
    tableHeaderTitleText1: {
        fontFamily: 'Inter',
        fontSize: 12,
        fontWeight: 500,
        width: '80%',
    },
    inclImage: {
        width: '103px',
        height: '119px',
    },
    inclImageText: {
        display: 'block',
        fontFamily: 'Inter',
        fontSize: 9,
        fontWeight: 500,
        lineHeight: 1.4,
        textAlign: 'right',
    },
    inclImageText2: {
        display: 'block',
        fontFamily: 'Inter',
        fontSize: 10,
        fontWeight: 600,
        lineHeight: 1.4,
        textAlign: 'center',
        paddingTop: 10,
    }
});

const getDynamicStyles = (coverData) => StyleSheet.create({
    paragraph: { fontFamily: 'Inter', fontSize: coverData?.["outroText-paragraph"] || 9, fontWeight: 400, marginBottom: 10 },
    bold: { fontFamily: 'Inter', fontWeight: 700 },
    italic: { fontFamily: 'Inter', fontStyle: 'italic', fontWeight: 400 },
    link: { fontFamily: 'Inter', fontWeight: 400, color: '#006cb7', textDecoration: 'underline' },
    h1: { fontFamily: 'Inter', fontSize: coverData?.["outroText-h1"] || 20, color: coverData?.["outroText-h1-color"] || "black", fontWeight: 700, marginBottom: 12 },
    h2: { fontFamily: 'Inter', fontSize: coverData?.["outroText-h2"] || 15, color: coverData?.["outroText-h2-color"] || "black", fontWeight: 700, marginBottom: 10, marginTop: 8 },
    h3: { fontFamily: 'Inter', fontSize: coverData?.["outroText-h3"] || 12, color: coverData?.["outroText-h3-color"] || "black", fontWeight: 600, marginBottom: 8 },
    h4: { fontFamily: 'Inter', fontSize: coverData?.["outroText-h4"] || 11, color: coverData?.["outroText-h4-color"] || "black", fontWeight: 600, marginBottom: 6 },
    h5: { fontFamily: 'Inter', fontSize: coverData?.["outroText-h5"] || 10, color: coverData?.["outroText-h5-color"] || "black", fontWeight: 600, marginBottom: 6 },
    ul: { marginLeft: 20, marginBottom: 10 },
    li: { fontFamily: 'Inter', fontWeight: 400, fontSize: coverData?.["outroText-list"] || 9, marginBottom: 4 },
});

const renderNode = (node, styles) => {
    if (node.type === 'text') {
        return <Text>{node.data}</Text>;
    }

    switch (node.name) {
        case 'p':
            return <Text wrap={false} style={styles.paragraph}>{node.children.map(child => renderNode(child, styles))}</Text>;
        case 'b':
            return <Text style={styles.bold}>{node.children.map(child => renderNode(child, styles))}</Text>;
        case 'i':
            return <Text style={styles.italic}>{node.children.map(child => renderNode(child, styles))}</Text>;
        case 'a':
            return (
                <Link style={styles.link} src={node.attribs.href}>
                    {node.children.map(child => renderNode(child, styles))}
                </Link>
            );
        case 'br':
            return <Text>{'\n'}</Text>;
        case 'h1':
            return <Text wrap={false} style={styles.h1}>{node.children.map(child => renderNode(child, styles))}</Text>;
        case 'h2':
            return <Text wrap={false} style={styles.h2}>{node.children.map(child => renderNode(child, styles))}</Text>;
        case 'h3':
            return <Text wrap={false} style={styles.h3}>{node.children.map(child => renderNode(child, styles))}</Text>;
        case 'h4':
            return <Text wrap={false} style={styles.h4}>{node.children.map(child => renderNode(child, styles))}</Text>;
        case 'h5':
            return <Text wrap={false} style={styles.h5}>{node.children.map(child => renderNode(child, styles))}</Text>;
        case 'ul':
            return <View wrap={false} style={styles.ul}>{node.children.map(child => renderNode(child, styles))}</View>;
        case 'li':
            return <Text wrap={false} style={styles.li}>• {node.children.map(child => renderNode(child, styles))}</Text>;
        default:
            return <Text>{node.children.map(child => renderNode(child, styles))}</Text>;
    }
};

const renderHTMLToPDF = (htmlString, styles) => {
    if (!htmlString || typeof htmlString !== 'string') {
        return [<Text style={{ fontFamily: 'Inter', fontSize: 8, }}>No content to display</Text>];
    }
    const parsedHTML = parseDocument(htmlString);
    return parsedHTML.children.map(node => renderNode(node, styles));
};

const AnswersGuideInfoTextQuinary = ({ coverData, answersData, categoriesData, matchCategory, uiData }) => {

    const [matchedCategory, setMatchedCategory] = useState(null);
    const [answersFromCategory, setAnswersFromCategory] = useState([]);
    const current = uiData.current;
    const currentText = `(${current})`;

    const roundToTwoDecimals = (num) => {
        if (isNaN(num) || num === null) {
            return "0.0";
        }
        const rounded = Math.round(num * 100) / 100;
        return rounded % 1 === 0 ? `${rounded}.0` : rounded.toFixed(1);
    };

    const getBackgroundColor = (roundedPoints, averagePoints) => {
        if (roundedPoints < averagePoints - 1) {
            return 'rgb(220, 38, 38)';
        } else if (roundedPoints >= averagePoints) {
            return 'rgb(77, 124, 15)';
        } else {
            return '#eac714';
        }
    };

    useEffect(() => {
        if (answersData && categoriesData) {
            const categoryData = categoriesData.find(item => item.name === matchCategory);
            setMatchedCategory(categoryData);

            if (matchCategory === uiData.lowest) {
                const allAnswers = [...answersData];
                const lowestPoints = allAnswers.sort((a, b) => a.points - b.points);
                const result = lowestPoints.slice(0, 10);
                setAnswersFromCategory(result);
            }
        }
    }, [answersData, categoriesData, uiData.lowest]);

    if (!coverData) {
        return <Text style={{ fontFamily: 'Inter', fontSize: 8 }}>No content to display</Text>;
    }

    const stylesoutroText = getDynamicStyles(coverData);
    const averagePoints = roundToTwoDecimals(coverData.avgPoint);

    return answersFromCategory.map(({ id, answer, points, outroText }) => {

        const roundedPoints = roundToTwoDecimals(points);
        const xPos = ((roundedPoints - 1) / 4) * 100;

        return (
            <Page wrap size='A4' key={id}>
                <Header />
                <View style={{ width: '100%' }}>
                    <View style={styles.tableHeader}>
                        <View style={styles.tableHeaderTitle}>
                            <Text style={[styles.tableHeaderTitleText1, { paddingRight: 5, width: 'auto' }]}>                            
                                F{(id >= 10 ? id + '.' : '0' + id + '.')}
                            </Text>
                            <Text style={styles.tableHeaderTitleText1}>
                                {answer}
                            </Text>
                        </View>
                        {answer && (
                            <View
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    color: '#ffffff',
                                    fontFamily: 'Inter',
                                    fontSize: 16,
                                    fontWeight: 700,
                                    padding: 5,
                                    width: 35,
                                    height: '100%',
                                    backgroundColor: getBackgroundColor(roundedPoints, averagePoints),
                                }}
                            >
                                <Text>{roundedPoints}</Text>
                            </View>
                        )}
                    </View>
                    <View style={{ position: 'relative', height: 30, width: '100%' }} fixed />
                    <View style={{ paddingLeft: '10%', paddingRight: '9%' }}>
                        {renderHTMLToPDF(outroText, stylesoutroText)}
                    </View>
                    <View style={{ position: 'relative', height: 30, width: '100%' }} fixed />
                </View>
                <FooterDynamic uiData={uiData} />
            </Page>
        );
    });
}

export default AnswersGuideInfoTextQuinary